import { inject, ref } from 'vue'

import { AuthError, isAuthError } from '@/domain/auth'
import { AUTH_API_KEY, OIDC_LAUNCHER_KEY } from '@/ports'
import type { AuthApi } from '@/ports/auth-api'

import { useSessionStore } from './session.store'

/**
 * useLogin : composable qui orchestre login local + déclenchement OIDC.
 *
 * Pour le login local :
 *   1. POST /auth/login {email, password} via AuthApi.loginLocal()
 *   2. Si succès, GET /auth/me pour récupérer le csrf_token
 *   3. Met à jour le store en 'authenticated'
 *
 * Pour le SSO OIDC :
 *   - Délègue à OidcLauncher.start(redirect) — top-level navigation,
 *     le SPA est complètement reload après callback.
 *
 * État réactif exposé :
 *   - `isLoading` : true pendant le call HTTP
 *   - `error`     : AuthError | null — reset à chaque submit
 */
export function useLogin() {
    const store = useSessionStore()
    const authApi = injectAuthApi()
    const oidcLauncher = inject(OIDC_LAUNCHER_KEY, null)

    const isLoading = ref(false)
    const error = ref<AuthError | null>(null)

    /**
     * loginLocal : tente le login email/password. Retourne true en cas
     * de succès, false sinon (l'erreur est dans `error.value` —
     * pattern "do not throw from event handlers").
     *
     * En cas de succès, le store est mis à jour avec le user + csrf.
     * Le caller doit alors naviguer (router.push) vers la destination.
     */
    async function loginLocal(email: string, password: string): Promise<boolean> {
        if (isLoading.value) {
            // Re-soumissions rapides ignorées : évite les doubles cookies.
            return false
        }
        isLoading.value = true
        error.value = null
        try {
            await authApi.loginLocal(email, password)
            // Le login pose le cookie session, mais ne renvoie pas le
            // csrf_token. On enchaîne /auth/me pour le récupérer avant
            // de marquer la session 'authenticated'.
            const session = await authApi.me()
            store.setAuthenticated(session)
            return true
        } catch (cause) {
            error.value = isAuthError(cause)
                ? cause
                : new AuthError('unknown', 'Unexpected login failure')
            return false
        } finally {
            isLoading.value = false
        }
    }

    /**
     * startOIDC : déclenche le redirect vers `/auth/oidc/login`. Le
     * caller passe le path interne où renvoyer l'user après login (par
     * défaut "/"). Filtré côté backend (open-redirect protection).
     */
    function startOIDC(redirect = '/'): void {
        if (!oidcLauncher) {
            // Pas d'OIDC configuré côté front (bootstrap volontaire) :
            // le composant LoginView doit alors masquer le bouton SSO.
            // Si on arrive ici c'est un appel illégitime — on signale.
            error.value = new AuthError('local_auth_disabled', 'OIDC not configured')
            return
        }
        oidcLauncher.start(redirect)
    }

    /**
     * clearError : utile pour les composants qui veulent reset l'erreur
     * en réponse à un input (UX : on enlève le message dès que le user
     * recommence à taper son password).
     */
    function clearError(): void {
        error.value = null
    }

    return {
        isLoading,
        error,
        loginLocal,
        startOIDC,
        clearError,
        hasOIDC: oidcLauncher !== null,
    }
}

function injectAuthApi(): AuthApi {
    const api = inject(AUTH_API_KEY, null)
    if (!api) {
        throw new AuthError('internal_error', 'AuthApi not provided (bootstrap bug)')
    }
    return api
}
